import { PARSE_INT_RADIX } from './misc';
import { isNullOrUndefined, normalizeNumericInput } from './base';
import { addPrefixZero } from './time';
export default class VideoTime {
    constructor(totalSeconds) {
        this.isNegative = totalSeconds < 0;
        this.totalSeconds = this.isNegative
            ? totalSeconds * -1
            : totalSeconds;
    }
    static fromSeconds(seconds) {
        return new VideoTime(seconds);
    }
    static parse(input, fallback = undefined) {
        const numericValue = normalizeNumericInput(input);
        if (numericValue !== null) {
            return VideoTime.fromSeconds(numericValue);
        }
        if (typeof input === 'string') {
            return VideoTime.fromTimeString(input, fallback);
        }
        console.error('Invalid input', input);
        throw new Error('Invalid input');
    }
    /**
     * Parses a time string and create a new instance
     *
     * @see {VideoTime.test()} for the expected inputs and outputs
     */
    static fromTimeString(input, fallback = undefined) {
        var _a, _b, _c, _d;
        let timeString = input.trim();
        if (timeString === '') {
            if (isNullOrUndefined(fallback) && typeof window.HiHaHoPlayer === 'object') {
                fallback = window.HiHaHoPlayer.videoStartTimeConsideringRequestedTime();
            }
            return new VideoTime(fallback !== null && fallback !== void 0 ? fallback : 0);
        }
        const amountColons = ((_a = timeString.match(/:/g)) !== null && _a !== void 0 ? _a : []).length;
        if (amountColons === 0) {
            const numericValue = normalizeNumericInput(input);
            if (numericValue !== null) {
                return VideoTime.fromSeconds(numericValue);
            }
        }
        timeString = timeString.replace(',', '.');
        if (amountColons > 2) {
            const lastColonIndex = timeString.lastIndexOf(':');
            timeString = `${timeString.substring(0, lastColonIndex)}.${timeString.substring(lastColonIndex + 1)}`;
        }
        if (timeString === '') {
            if (isNullOrUndefined(fallback) && typeof window.HiHaHoPlayer === 'object') {
                fallback = window.HiHaHoPlayer.videoStartTimeConsideringRequestedTime();
            }
            return new VideoTime(fallback !== null && fallback !== void 0 ? fallback : 0);
        }
        // [seconds, minutes, hours]
        const timeComponents = timeString.split(':').reverse();
        const seconds = parseFloat((_b = timeComponents[0]) !== null && _b !== void 0 ? _b : '0');
        const minutes = parseInt((_c = timeComponents[1]) !== null && _c !== void 0 ? _c : '0', PARSE_INT_RADIX);
        const hours = parseInt((_d = timeComponents[2]) !== null && _d !== void 0 ? _d : '0', PARSE_INT_RADIX);
        return new VideoTime(hours * 3600 + minutes * 60 + seconds);
    }
    totalMilliseconds() {
        return this.totalSeconds * 1000;
    }
    fractionalMilliseconds() {
        return Math.round((this.totalSeconds % 1) * 1000);
    }
    components() {
        const totalSeconds = Math.floor(this.totalSeconds);
        let hours = Math.floor(totalSeconds / 3600);
        let minutes = Math.floor((totalSeconds % 3600) / 60);
        let seconds = Math.floor(totalSeconds % 60);
        let ms = this.fractionalMilliseconds();
        if (ms >= 995) {
            seconds++;
            ms = 0;
        }
        while (seconds >= 60) {
            minutes++;
            seconds -= 60;
        }
        while (minutes >= 60) {
            hours++;
            minutes -= 60;
        }
        return {
            hours,
            minutes,
            seconds,
            ms,
            isNegative: this.isNegative,
        };
    }
    formatHideHoursWhenZero(studioNotation = true, millisecondsLength = null) {
        if (millisecondsLength === null) {
            millisecondsLength = studioNotation ? 2 : 0;
        }
        return this.format(true, millisecondsLength, studioNotation);
    }
    formatForceHours(studioNotation = true, millisecondsLength = null) {
        if (millisecondsLength === null) {
            millisecondsLength = studioNotation ? 2 : 0;
        }
        return this.format(false, millisecondsLength, studioNotation);
    }
    format(hideHoursWhenZero, millisecondsLength, studioNotation = true) {
        const components = this.components();
        const prefix = this.isNegative ? '-' : '';
        const hideHours = hideHoursWhenZero && components.hours === 0;
        let value = prefix;
        if (!hideHours) {
            if (studioNotation) {
                value += addPrefixZero(components.hours);
            }
            else {
                value += components.hours;
            }
            value += ':';
        }
        const minutesComponent = hideHours && !studioNotation ? components.minutes : addPrefixZero(components.minutes);
        value += `${minutesComponent}:${addPrefixZero(components.seconds)}`;
        if (millisecondsLength > 0) {
            const ms = Math.round(components.ms * Math.pow(10, millisecondsLength) / 1000); // Scale and truncate
            value += `.${String(ms).padStart(millisecondsLength, '0')}`;
        }
        return value;
    }
    static test() {
        const tests = {
            '1': '00:00:01.00',
            '1.1': '00:00:01.10',
            '1.01': '00:00:01.01',
            '1.115': '00:00:01.12',
            '1,1': '00:00:01.10',
            '1,01': '00:00:01.01',
            '1,115': '00:00:01.12',
            '101': '00:01:41.00',
            '101.25': '00:01:41.25',
            '1:01': '00:01:01.00',
            '10101': '02:48:21.00',
            '19901.3': '05:31:41.30',
            '010101.01': '02:48:21.01',
            '00:01:59.680': '00:01:59.68',
            '00:23:01.00': '00:23:01.00',
            '02:23:01.00': '02:23:01.00',
            '00:65:14.41': '01:05:14.41',
            '00:65:14,41': '01:05:14.41',
            '00:65:14:41': '01:05:14.41',
            '00:08:44:60': '00:08:44.60',
            '1.996': '00:00:02.00',
            '1.999999999': '00:00:02.00',
            '1.994499999': '00:00:01.99',
        };
        let amountErrors = 0;
        Object.entries(tests).forEach(([input, expected], index) => {
            const actual = VideoTime.fromTimeString(input).formatForceHours();
            if (actual !== expected) {
                console.error(`Failed test at index ${index}: Input "${input}" expected "${expected}", but got "${actual}"`);
                amountErrors++;
            }
        });
        const amountTests = Object.keys(tests).length;
        if (amountErrors > 0) {
            console.error(`Failed ${amountErrors}/${amountTests} tests`);
        }
        else {
            console.log(`All ${amountTests} tests passed`);
        }
    }
}
